import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { Site } from "@inrange/building-manager-api-client/models-site";
import React from "react";
import {
  formatTariff,
  fractionalCurrencySymbol,
} from "../../formatting/currency-utils";
import { formatCurrencyToNearestTen } from "../../formatting/formatCurrency";
import { formatUnitsToNearestTen } from "../../formatting/formatKiloUnits";
import formatPercentage from "../../formatting/formatPercentage";
import {
  ColorHighlighted,
  HiddenOfferBullet,
  OfferComparisonSectionBefore,
  OfferDetailHeader,
  OfferList,
} from "./marketplace-styles";
import { getIRR, getRevenue } from "./marketplace-utils";

const YourSiteTodayBeforeSellOffer: React.FC<{
  org: Organisation;
  site: Site;
  renderBlankOfferRow: boolean;
}> = ({ org, site, renderBlankOfferRow }) => {
  const totalExportableEnergy =
    site.energyFlowAnnual.networkExport +
    site.energyFlowAnnual.exported +
    (site.hasAllZeroExportLimit ? site.energyFlowAnnual.curtailed : 0);
  const networkExportSdmMatches = [
    ...site.sdmMatches,
    ...(site.energyFlowAnnual.exported > 0
      ? [
          {
            sellerId: site.id,
            buyerId: "spill",
            volume: site.energyFlowAnnual.exported,
            tariff: site.tariffConfig.spillExportPPA.tariff,
          },
        ]
      : []),
  ]
    .filter((match) => match.sellerId === site.id)
    .sort((a, b) => {
      const aTariff = a.tariff;
      const bTariff = b.tariff;
      // Sort by tariff - highest to lowest
      if (aTariff !== bTariff) {
        return bTariff - aTariff;
      }
      // Otherwise fall back to sorting by volume descending
      return b.volume - a.volume;
    });
  const totalSoldEnergy = networkExportSdmMatches.reduce(
    (acc, match) => acc + match.volume,
    0
  );
  return (
    <OfferComparisonSectionBefore>
      <OfferDetailHeader>
        <ColorHighlighted>Your site today</ColorHighlighted>
      </OfferDetailHeader>
      <div>
        {totalExportableEnergy === 0 && <>0 kWh energy exported</>}
        {totalExportableEnergy > 0 && (
          <>
            {networkExportSdmMatches.length} buyer
            {(networkExportSdmMatches.length > 1 ||
              networkExportSdmMatches.length == 0) &&
              "s"}{" "}
            of{" "}
            {formatPercentage(totalSoldEnergy / totalExportableEnergy, 1, true)}{" "}
            of your exportable energy
            <OfferList>
              {networkExportSdmMatches.map((match) => (
                <li key={match.buyerId}>
                  {formatUnitsToNearestTen(match.volume, "kWh")} @{" "}
                  {(match.tariff * 100).toFixed(2)}
                  {fractionalCurrencySymbol(site.currencyCode)}
                </li>
              ))}
              {renderBlankOfferRow && (
                <HiddenOfferBullet>&nbsp;</HiddenOfferBullet>
              )}
            </OfferList>
          </>
        )}
      </div>
      <div>
        {formatUnitsToNearestTen(site.energyFlowAnnual.behindMeter, "kWh")} sold
        on-site @{" "}
        {formatTariff(
          site.tariffConfig.onsitePPA.tariff,
          site.tariffConfig.onsitePPA.ppaPurchaseModel,
          site.tariffConfig.onsitePPA.ppaLeaseBillingFrequency,
          org.orgType === "Landlord",
          site.currencyCode,
          false,
          true
        )}
      </div>
      <div>{formatPercentage(getIRR(site, site.financialModels))} IRR</div>
      <div>
        {formatCurrencyToNearestTen(
          getRevenue(site, site.financialModels),
          site.currencyCode,
          0,
          false
        )}{" "}
        revenue / year
      </div>
    </OfferComparisonSectionBefore>
  );
};

export default YourSiteTodayBeforeSellOffer;
